import { CHUNK_TYPE } from '~/constants/conversationModels';

import { getSecondOrderReferencesBySnippet } from '~/utils/citation-second-order';

/**
 * A single citation placeholder occurrence, parsed from `[docId(-N), p.M]` text or a
 * `.citation-placeholder` DOM element.
 * @typedef {object} CitationEntry
 * @property {string} id - Document externalId (the snippet-index suffix already stripped off).
 * @property {string} snippetIndex - Snippet index parsed from the placeholder's trailing `-N`, or '0' when absent.
 * @property {string} page - Page number, as captured from the placeholder text.
 */

// regex for matching citation placeholders in raw message text.
export const CITATION_PLACEHOLDER_RGX = /\[([^\]\n,]+),\s*p\.(\d+)\]/g;

/**
 * Splits a raw citation placeholder id into the document externalId and its snippet index.
 * The snippet index is an optional trailing `-N` suffix (e.g. `docId-2` → id `docId`, snippetIndex `2`);
 * a trailing segment is only treated as the index when it's entirely digits, so document ids that
 * themselves end in a hyphenated number without a snippet suffix fall back to snippetIndex '0'.
 * @param {string} rawId - The id portion captured by CITATION_PLACEHOLDER_RGX, before the `, p.N`.
 * @returns {{id: string, snippetIndex: string}} The split id and snippet index (see CitationEntry).
 */
export const parseCitationPlaceholderId = (rawId = '') => {
  const separatorIndex = rawId.lastIndexOf('-');
  if (separatorIndex === -1) {
    return { id: rawId, snippetIndex: '0' };
  }

  const possibleSnippetIndex = rawId.slice(separatorIndex + 1);
  const isSnippetIndex =
    possibleSnippetIndex.length > 0 && [...possibleSnippetIndex].every((char) => char >= '0' && char <= '9');

  if (!isSnippetIndex) {
    return { id: rawId, snippetIndex: '0' };
  }

  return {
    id: rawId.slice(0, separatorIndex),
    snippetIndex: possibleSnippetIndex,
  };
};

/**
 * Extracts every `[docId(-N), p.M]` citation placeholder from raw message text, in text order.
 * @param {string} content - Raw markdown/message text to scan.
 * @returns {CitationEntry[]} Placeholders found, in text order.
 */
export const extractCitationEntries = (content = '') => {
  const entries = [];

  for (const [, rawId, page] of content.matchAll(CITATION_PLACEHOLDER_RGX)) {
    const { id, snippetIndex } = parseCitationPlaceholderId(rawId);
    entries.push({ id, snippetIndex, page });
  }

  return entries;
};

/**
 * Extracts the unique set of documentIds cited in a message's raw text, in first-seen order.
 * Used to build the legacy `citedReferences` list (one entry per distinct cited document, not
 * per second-order source).
 * @param {string} message - Raw message text.
 * @returns {{documentId: string}[]} One entry per distinct cited document, in first-seen order.
 */
export const extractReferencesFromCitations = (message = '') => {
  const citations = [];

  for (const [, rawId, page] of message.matchAll(CITATION_PLACEHOLDER_RGX)) {
    const { id } = parseCitationPlaceholderId(rawId);
    citations.push({ documentId: id, page: parseInt(page, 10) });
  }

  return citations.reduce((acc, citation) => {
    if (!acc.find((existing) => existing.documentId === citation.documentId)) {
      acc.push({ documentId: citation.documentId });
    }
    return acc;
  }, []);
};

const getRenderedChunkContents = (message = {}) => {
  if (!message.chunks?.length) {
    return [message.message || ''];
  }

  return message.chunks.flatMap((chunk) => {
    if (chunk.type === CHUNK_TYPE.TEXT) {
      return [chunk.content].filter(Boolean);
    }
    return [];
  });
};

/**
 * All citation placeholder entries across a whole message (both legacy `message.message` and
 * chunked/new-agent messages), in document order.
 * @param {object} message - A conversation message; either `message.message` (legacy) or
 *   `message.chunks` (new-agent) is read, whichever the message shape provides.
 * @returns {CitationEntry[]} Placeholders found, in text order.
 */
export const getRenderedCitationEntries = (message = {}) =>
  getRenderedChunkContents(message).flatMap((content) => extractCitationEntries(content || ''));

// Same cited-references matching used by processCitedReferences (legacy blocks), but sourced from
// the rendered message text/chunks directly so it also works for SSE messages that never go through
// processCitedReferences.
export const buildCitedReferences = (message = {}, references = []) => {
  const messageText = getRenderedChunkContents(message).join('');
  const citedRefs = extractReferencesFromCitations(messageText);

  return citedRefs.map((citedRef) => references.find((ref) => ref.externalId === citedRef.documentId)).filter(Boolean);
};

/**
 * Shared core for buildRefNumCitationDict and buildCitationDictFromPlaceholders. Walks citation
 * entries in document order and assigns each a running label number (1, 2, 3, ...), reusing the
 * same number if that exact key is seen again later. Two kinds of keys:
 * - `${id}-${text||referenceId}` for citations backed by real second-order data — grouped by the
 *   citation's own text rather than its (physical, coordinate-based) referenceId, so the same
 *   source repeated at multiple locations (e.g. a footer reference list on every page) shares one
 *   label instead of getting a new one per occurrence.
 * - `${id}-${snippetIndex}` for citations with no second-order data for that snippet (repeat
 *   citations of the same doc+snippet stay numbered consistently).
 * @param {CitationEntry[]} entries - Citation occurrences, in order.
 * @param {object[]} references - Document reference objects (carrying second_order_references_by_snippet).
 * @returns {Record<string, number>} Map of composite key → assigned label.
 */
const _buildRefNumDictFromEntries = (entries = [], references = []) => {
  const dict = {};
  let label = 1;

  for (const { id, snippetIndex, page } of entries) {
    const snippetKey = `${page}-${snippetIndex}`;
    const secondOrderRefs = references
      .filter((ref) => ref.externalId === id)
      .flatMap((ref) => getSecondOrderReferencesBySnippet(ref)[snippetKey] || []);

    if (secondOrderRefs.length > 0) {
      // Group by the citation's own text (not referenceId) — the same source can appear at
      // multiple physical locations (e.g. a reference list repeated in a footer on every page),
      // each with a distinct coordinate-based referenceId, but it's still the same source.
      secondOrderRefs.forEach(({ referenceId, text }) => {
        const dictKey = `${id}-${text || referenceId}`;
        if (!(dictKey in dict)) {
          dict[dictKey] = label++;
        }
      });
    } else if (!(`${id}-${snippetIndex}` in dict)) {
      dict[`${id}-${snippetIndex}`] = label++;
    }
  }

  return dict;
};

/**
 * Builds the citation label dict for a rendered message (see _buildRefNumDictFromEntries).
 * @param {CitationEntry[]} renderedCitationEntries - Placeholders found in the message, in order.
 * @param {object[]} references - Document reference objects for this message.
 * @returns {Record<string, number>} Map of composite key → assigned label.
 */
export const buildRefNumCitationDict = (renderedCitationEntries = [], references = []) =>
  _buildRefNumDictFromEntries(renderedCitationEntries, references);

/**
 * Builds the citation label dict (see _buildRefNumDictFromEntries) from DOM `.citation-placeholder`
 * elements instead of parsed text entries — used by TextMessageWithTooltip.vue after
 * sanitizeAndParse has turned raw `[id-N, p.M]` markers into placeholder spans.
 * @param {NodeListOf<Element>|Element[]} placeholders - Elements with data-id/data-index/data-page.
 * @param {object[]} references - Document reference objects for this message.
 * @returns {Record<string, number>} Map of composite key → assigned label.
 */
export const buildCitationDictFromPlaceholders = (placeholders, references = []) => {
  const entries = Array.from(placeholders).map((ph) => ({
    id: ph.getAttribute('data-id'),
    snippetIndex: ph.getAttribute('data-index'),
    page: ph.getAttribute('data-page'),
  }));
  return _buildRefNumDictFromEntries(entries, references);
};
